import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log('Error caught in error interceptor:', error);

        // ไม่แสดงข้อความแจ้งเตือนสำหรับการดาวน์โหลดไฟล์
        const isDownloadRequest = request.url.includes('/exportPdf') ||
          request.url.includes('/exportExcel');

        if (!isDownloadRequest) {
          const errorMessage = this.handleError(error);
          console.log('Showing error message in snackbar:', errorMessage);

          this.snackBar.open(errorMessage, 'ปิด', {
            duration: 5000,
            panelClass: '_warning'
          });

          // ถ้าเป็นข้อผิดพลาด 401 Unauthorized ให้ logout อัตโนมัติ
          // (AuthInterceptor จะจัดการเรื่องการ redirect แล้ว)
          if (error.status === 401) {
            setTimeout(() => {
              this.authService.logout();
            }, 1000);
          }
        }

        return throwError(() => error);
      })
    );
  }

  private handleError(error: HttpErrorResponse): string {
    console.log('Processing error in handleError:', error);

    let errorMessage = 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `เกิดข้อผิดพลาด: ${error.error.message}`;
    } else {
      // Server-side error
      console.log('Backend error status:', error.status);
      console.log('Error body:', error.error);

      switch (error.status) {
        case 0:
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ โปรดตรวจสอบการเชื่อมต่อของคุณ';
          break;
        case 400:
          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (typeof error.error === 'object') {
              // เรียงลำดับความสำคัญ: ตรวจสอบ details ก่อน message เพื่อให้แสดงข้อความภาษาไทย
              if (error.error.details && Array.isArray(error.error.details) && error.error.details.length > 0) {
                errorMessage = error.error.details[0];
              } else if (error.error.message) {
                errorMessage = error.error.message;
              } else if (error.error.error) {
                errorMessage = error.error.error;
              } else if (error.error.errorMessage) {
                errorMessage = error.error.errorMessage;
              } else {
                const firstErrorKey = Object.keys(error.error)[0];
                if (firstErrorKey && error.error[firstErrorKey]) {
                  if (typeof error.error[firstErrorKey] === 'string') {
                    errorMessage = error.error[firstErrorKey];
                  } else if (Array.isArray(error.error[firstErrorKey])) {
                    errorMessage = error.error[firstErrorKey][0];
                  }
                } else {
                  errorMessage = 'คำขอไม่ถูกต้อง';
                }
              }
            } else {
              errorMessage = 'คำขอไม่ถูกต้อง';
            }
          } else {
            errorMessage = 'คำขอไม่ถูกต้อง';
          }
          break;
        case 401:
          errorMessage = 'กรุณาเข้าสู่ระบบใหม่อีกครั้ง เนื่องจากหมดเวลาการใช้งาน';
          break;
        case 403:
          errorMessage = 'ไม่มีสิทธิ์เข้าถึงข้อมูล';
          break;
        case 404:
          errorMessage = 'ไม่พบข้อมูลที่ต้องการ';
          break;
        case 409:
          if (error.error && typeof error.error === 'object' && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error && typeof error.error === 'string') {
            errorMessage = error.error;
          } else {
            errorMessage = 'ข้อมูลมีความขัดแย้งกัน';
          }
          break;
        case 422:
          if (error.error && typeof error.error === 'object' && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error && typeof error.error === 'string') {
            errorMessage = error.error;
          } else {
            errorMessage = 'ข้อมูลไม่สามารถประมวลผลได้';
          }
          break;
        case 500:
          errorMessage = 'เกิดข้อผิดพลาดจากระบบ กรุณาติดต่อผู้ดูแลระบบ';
          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (typeof error.error === 'object') {
              if (error.error.details && Array.isArray(error.error.details) && error.error.details.length > 0) {
                errorMessage = error.error.details[0];
              } else if (error.error.message) {
                errorMessage = error.error.message;
              } else if (error.error.error) {
                errorMessage = error.error.error;
              }
            }
          }
          break;
        case 502:
          errorMessage = 'เซิร์ฟเวอร์ไม่พร้อมใช้งาน (Bad Gateway)';
          break;
        case 503:
          errorMessage = 'บริการไม่พร้อมใช้งานชั่วคราว โปรดลองอีกครั้งในภายหลัง';
          break;
        case 504:
          errorMessage = 'การเชื่อมต่อกับเซิร์ฟเวอร์หมดเวลา โปรดลองอีกครั้ง';
          break;
        default:
          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (typeof error.error === 'object') {
              if (error.error.details && Array.isArray(error.error.details) && error.error.details.length > 0) {
                errorMessage = error.error.details[0];
              } else if (error.error.message) {
                errorMessage = error.error.message;
              } else if (error.error.error) {
                errorMessage = error.error.error;
              }
            }
          } else if (error.message) {
            errorMessage = error.message;
          } else {
            errorMessage = `เกิดข้อผิดพลาด: ${error.status} ${error.statusText}`;
          }
          break;
      }
    }
    console.log('Final error message to display:', errorMessage);

    return errorMessage;
  }
}
